import React from 'react';
import { fieldProps } from './missingFields';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { generateAndDownloadNDA, generateAndDownloadSQA, generateAndDownload4MDeclaration, generateAndDownloadCodeOfConduct, generateAndDownloadComplianceAgreement, generateAndDownloadSelfDeclaration, generateAndDownloadOverseasAgreement } from '../../../utils/documentGenerator.jsx';
import { isIndia } from '../../../utils/countries.js';
import { StepHeader } from './StepHeader';

const AgreementStep = ({ formData, updateFormData, errors, onGeneratePDF, onPreviewDocument, keepExistingDocuments, setKeepExistingDocuments, uploadedFiles }) => {
  // Helper function to check if supplier is ODM
  const isODMSupplier = () => {
    return formData.supplierGroup === 'odm-amber';
  };

  // Helper function to check if supplier is Indian
  const isIndianSupplier = () => {
    return isIndia(formData.countryOrigin);
  };

  const agreements = [
    {
      id: 'nda',
      title: 'Mutual Non-Disclosure Agreement (NDA)',
      shortName: 'NDA',
      description: `I agree to the Mutual Confidentiality and Non-Disclosure Agreement with Amber Enterprises India Limited.\n\nKey terms:\n• Both parties keep each other's Confidential Information confidential and use it only for the Purpose\n• Disclosure only to employees, consultants and advisors on a need-to-know basis\n• Confidential Information remains the property of the disclosing party; no licence is granted\n• Valid for 1 year from the Effective Date; confidentiality survives for 1 year after\n• Governed by the laws of India; disputes resolved by arbitration seated in Delhi`,
      required: true,
      canGenerate: true,
      generateFunction: () => generateAndDownloadNDA(formData),
      shouldShow: () => !isODMSupplier() // Show when NOT ODM
    },
    {
      id: 'sqa',
      title: 'Supplier Agreement',
      description: `The Supplier agrees to uphold the highest standards in the performance of its obligations under this Agreement.\n\n This includes, without limitation\n• Compliance with all specifications, requirements, and guidelines\n• Implementation of appropriate processes and controls to ensure consistent performance.\n• Engaging in continuous improvement initiatives to enhance efficiency, reliability.\n• Immediate notification of quality issues\n• Continuous improvement initiatives`,
      required: true,
      canGenerate: true,
      generateFunction: () => generateAndDownloadSQA(formData),
      shouldShow: () => isIndianSupplier() // Show when Indian supplier
    },
    {
      id: 'fourM',
      title: '4M Change Control Agreement',
      description: `I agree to notify Amber Enterprises India Limited of any changes in the 4M parameters:\n\n• Man (Personnel changes)\n• Machine (Equipment modifications)\n• Material (Raw material changes)\n• Method (Process modifications)\n\nAll changes must be approved before implementation.`,
      required: true,
      canGenerate: true,
      generateFunction: () => generateAndDownload4MDeclaration(formData),
      shouldShow: () => true // Always show
    },
    {
      id: 'codeOfConduct',
      title: 'Code of Conduct for Business Partners',
      shortName: 'Code of Conduct',
      description: `I agree to adhere to the Amber Group Code of Conduct for Business Partners (Ver. 1, 2nd Sep 2026).\n\nThis covers:\n• Labor and human rights, health, safety and environment\n• Anti-bribery, anti-corruption, conflicts of interest and fair competition\n• Gifts and hospitality limits, protection of information and data privacy\n• Anti-counterfeit measures, no retaliation and insider trading\n• Audit, indemnification and consequences of violation`,
      required: true,
      canGenerate: true,
      generateFunction: () => generateAndDownloadCodeOfConduct(formData),
      shouldShow: () => true // Always show
    },
    {
      id: 'complianceAgreement',
      title: 'Compliance Agreement',
      description: `I confirm compliance with all applicable laws, regulations, and industry standards.\n\nThis includes:\n• Local and international trade laws\n• Environmental regulations\n• Labor laws and worker rights\n• Tax and financial regulations\n• Industry-specific compliance requirements`,
      required: true,
      canGenerate: true,
      generateFunction: () => generateAndDownloadComplianceAgreement(formData),
      shouldShow: () => isIndianSupplier() // Show when Indian supplier
    },
    {
      id: 'selfDeclaration',
      title: 'Self Declaration',
      description: `I hereby declare that:\n\n• All information provided in this registration is true and accurate\n• I have the authority to enter into this agreement\n• My organization has the capability to fulfill the requirements\n• I will promptly notify of any material changes\n• I understand the consequences of providing false information`,
      required: true,
      canGenerate: true,
      generateFunction: () => generateAndDownloadSelfDeclaration(formData),
      shouldShow: () => true // Always show
    },
    {
      id: 'overseasAgreement',
      title: 'Overseas Agreement',
      description: `I agree to the terms and conditions specified in the Overseas Agreement for foreign suppliers.\n\nThis agreement covers:\n• Terms and conditions for overseas suppliers\n• Compliance with international trade regulations\n• Quality and delivery commitments\n• Legal and regulatory requirements`,
      required: true,
      canGenerate: true,
      generateFunction: () => generateAndDownloadOverseasAgreement(formData),
      shouldShow: () => !isIndianSupplier() // Show only for foreign vendors
    }
  ];

  // Filter agreements based on display constraints
  const visibleAgreements = agreements.filter(agreement => agreement.shouldShow());

  const handleAgreementChange = (agreementId, checked) => {
    // The signature (uploaded in Step 2) is embedded into every agreement, so it must exist before
    // any agreement can be accepted.
    if (!hasSignature) return;
    const currentAgreements = formData.agreements || {};
    updateFormData({
      agreements: {
        ...currentAgreements,
        [agreementId]: checked
      }
    });
  };

  const handleGeneratePDF = (agreement) => {
    if (agreement.generateFunction) {
      agreement.generateFunction();
      // Track that this agreement's document has been downloaded — the final Submit is gated on
      // every accepted agreement having been downloaded (item 8).
      updateFormData({
        agreementDownloads: { ...(formData.agreementDownloads || {}), [agreement.id]: true },
      });
    }
  };

  const downloads = formData.agreementDownloads || {};
  const downloadedCount = visibleAgreements.filter((a) => downloads[a.id]).length;
  const allDownloaded = downloadedCount === visibleAgreements.length;

  // The digital signature is now captured in Step 2 (Company Information).
  const hasSignature = Boolean(formData.digitalSignature) || (keepExistingDocuments?.digitalSignature && uploadedFiles?.DIGITAL_SIGNATURE);

  return (
    <div className="space-y-6">
      <StepHeader
        title="Agreements & Declarations"
        description="Read, accept and download each agreement to complete your registration. Your digital signature (from the Company Information step) is embedded into each document."
      />

      {/* Legally-binding acceptance warning — PLACEHOLDER wording pending legal-team review.
          Do not treat as final; replace the paragraph below with the wording legal signs off on. */}
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
        <div className="flex items-start gap-3">
          <Icon name="Scale" className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" />
          <div className="text-sm text-foreground">
            <p className="font-semibold">Legally binding acceptance</p>
            <p className="mt-1 text-text-secondary">
              By ticking the box for an agreement below, you — as an authorised signatory — confirm that you have
              read, understood, and agree to be legally bound by that specific agreement on behalf of your
              organisation, with the same force and effect as a handwritten signature. Each ticked box is a
              separate, binding acceptance of that agreement between your organisation and Amber Enterprises
              India Limited. You must download and retain a copy of every agreement you accept; this registration
              cannot be submitted until each accepted agreement has been downloaded.
            </p>
          </div>
        </div>
      </div>

      {!hasSignature && (
        <div className="flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-foreground" role="status">
          <Icon name="Lock" className="h-4 w-4 flex-shrink-0 text-warning" />
          A digital signature is required — upload it in the Company Information step before accepting the agreements.
        </div>
      )}

      <div className="space-y-6">
        {visibleAgreements.map((agreement) => {
          const isDownloaded = Boolean(downloads[agreement.id]);
          const isChecked = Boolean(formData.agreements?.[agreement.id]);
          return (
            <div key={agreement.id} className="bg-muted/30 p-6 rounded-lg">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {agreement.title}
                    {agreement.required && <span className="text-destructive ml-1">*</span>}
                  </h3>
                  <p className="text-sm text-text-secondary whitespace-pre-line">
                    {agreement.description}
                  </p>
                </div>
                <div className="ml-4 flex flex-col items-end gap-2">
                  <Checkbox
                    {...fieldProps(`agreements.${agreement.id}`)}
                    checked={isChecked}
                    onCheckedChange={(checked) => handleAgreementChange(agreement.id, checked)}
                    required={agreement.required}
                    disabled={!hasSignature}
                  />
                  {agreement.canGenerate && isChecked && (
                    <>
                      <Button
                        variant={isDownloaded ? 'ghost' : 'outline'}
                        size="sm"
                        onClick={() => handleGeneratePDF(agreement)}
                        className="flex items-center space-x-2 whitespace-nowrap"
                      >
                        <Icon name="Download" className="w-4 h-4" />
                        <span>{isDownloaded ? 'Re-download' : 'Download'} {agreement.shortName || agreement.title.split(' ')[0]}</span>
                      </Button>
                      {isDownloaded && (
                        <span className="flex items-center gap-1 text-xs font-medium text-success">
                          <Icon name="CheckCircle2" className="h-3.5 w-3.5" /> Downloaded
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
              {errors[`agreements.${agreement.id}`] && (
                <p className="text-sm text-destructive mt-2">{errors[`agreements.${agreement.id}`]}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Download-before-submit progress */}
      <div
        className={`rounded-lg border p-4 ${allDownloaded ? 'border-success/30 bg-success/5' : 'border-border bg-muted/30'}`}
        role="status"
        {...fieldProps('agreementDownloads')}
        tabIndex={-1}
        aria-live="polite"
      >
        <div className="flex items-center gap-3">
          <Icon name={allDownloaded ? 'ShieldCheck' : 'FileDown'} className={`h-5 w-5 flex-shrink-0 ${allDownloaded ? 'text-success' : 'text-text-secondary'}`} />
          <div className="text-sm">
            <p className="font-medium text-foreground">
              Agreement documents downloaded: {downloadedCount} of {visibleAgreements.length}
            </p>
            <p className="text-text-secondary">
              {allDownloaded
                ? 'All agreements downloaded — you can submit your registration.'
                : "Accept and download every agreement above. Your registration can't be submitted until all are downloaded."}
            </p>
          </div>
        </div>
      </div>

      {errors.agreementDownloads && (
        <p className="text-sm text-destructive">{errors.agreementDownloads}</p>
      )}
    </div>
  );
};

export default AgreementStep;
